import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { AlertTriangle, ArrowLeft, Calculator, Package } from "lucide-react";

import { showConfirm } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { SearchableSelect } from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { getProductionUnitLabel } from "~/constants/units";
import { useExchangeRates } from "~/hooks/exchangeRates";
import {
  useAssemblyLines,
  useDefaultRecipeForProduct,
  useRecipeCost,
  useRecipes,
} from "~/hooks/production";
import { useCreateProductionBatch } from "~/hooks/production";
import {
  getStockAt,
  useOilTypes,
  useOilTypeStockByPlant,
  useRawMaterials,
  useRawMaterialStockByPlant,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { useProducts } from "~/hooks/useProducts";
import { useProfileStore } from "~/store/profileStore";
import type {
  BatchIngredientOverride,
  ProductionBatchFormData,
} from "~/types/production";
import type { Plant } from "~/types/purchasing";
import { UNIT_LABELS } from "~/types/purchasing";
import { formatCurrency } from "~/utils/helpers";
import { formatUsd } from "~/utils/oilConversions";

import { BatchIngredientsEditor } from "./components/BatchIngredientsEditor";

export function NewProductionBatchPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: userId } = useProfileStore();
  const createMutation = useCreateProductionBatch();

  // Get current exchange rate
  const { data: exchangeRates = [] } = useExchangeRates();
  const latestRate = exchangeRates[0]?.usd_clp || 900;

  // Assembly lines (Líneas de Envasado)
  const { data: assemblyLines = [] } = useAssemblyLines();

  // Products and recipes
  const { products } = useProducts();
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const selectedProduct = products.find((p) => p.id === selectedProductId);

  // Get default recipe for selected product
  const { data: defaultRecipe } = useDefaultRecipeForProduct(
    selectedProductId || undefined
  );

  // Get all recipes for the selected product
  const { data: productRecipes = [] } = useRecipes(
    selectedProductId ? { product_id: selectedProductId } : undefined
  );

  const [formData, setFormData] = useState<ProductionBatchFormData>({
    product_id: "",
    recipe_id: undefined,
    target_quantity: 100,
    start_date: dayjs().format("YYYY-MM-DD"),
    end_date: dayjs().add(5, "day").format("YYYY-MM-DD"),
    expiry_date: dayjs().add(365, "day").format("YYYY-MM-DD"),
    contains_allergens: false,
    notes: "",
    ingredient_overrides: [],
  });

  // State for ingredient overrides (separate for easier management)
  const [ingredientOverrides, setIngredientOverrides] = useState<
    BatchIngredientOverride[]
  >([]);

  // State for stock validation (global-cache check from the ingredients editor)
  const [hasInsufficientStock, setHasInsufficientStock] = useState(false);

  // Per-plant stock (source of truth) for the batch-creation sanity guard
  const { data: oilTypes = [] } = useOilTypes();
  const { data: rawMaterials = [] } = useRawMaterials();
  const { data: oilStockByPlant } = useOilTypeStockByPlant();
  const { data: rawStockByPlant } = useRawMaterialStockByPlant();

  // Explicit acknowledgement when planned consumption exceeds plant stock
  const [ackOverStock, setAckOverStock] = useState(false);

  // Auto-select default recipe when product changes
  useEffect(() => {
    if (defaultRecipe) {
      setFormData((prev) => ({
        ...prev,
        product_id: selectedProductId,
        recipe_id: defaultRecipe.id,
      }));
      setIngredientOverrides([]); // Reset overrides when recipe changes
    } else if (selectedProductId) {
      setFormData((prev) => ({
        ...prev,
        product_id: selectedProductId,
        recipe_id: undefined,
      }));
      setIngredientOverrides([]);
    }
  }, [defaultRecipe, selectedProductId]);

  // Get selected recipe with ingredients
  const selectedRecipe = productRecipes.find(
    (r) => r.id === formData.recipe_id
  );

  // Get cost breakdown for selected recipe
  const { data: costBreakdown } = useRecipeCost(formData.recipe_id, latestRate);

  // Calculate estimated total cost
  const estimatedCostClp = costBreakdown
    ? costBreakdown.total_cost_clp * formData.target_quantity
    : 0;
  const estimatedCostUsd = costBreakdown
    ? costBreakdown.total_cost_usd * formData.target_quantity
    : 0;
  const costPerBoxClp = costBreakdown?.total_cost_clp || 0;

  // --- Per-plant stock sanity guard --------------------------------------
  // Compare each ingredient's implied planned consumption against the
  // source-of-truth per-plant stock at the SELECTED plant. Soft guard only:
  // bulk/granel oils legitimately run negative by design, so the user can
  // override via an explicit acknowledgement checkbox.
  //
  // Oils: recipe/planned quantity is in KG, but per-plant oil stock is in
  // LITERS. We convert available liters → kg via density (liters * density)
  // and compare in kg — matching BatchIngredientsEditor.getStockInfo so both
  // surfaces agree.
  const overStockIngredients = useMemo(() => {
    const plant = formData.plant as Plant | undefined;
    if (!plant) return [] as {
      key: string;
      name: string;
      needed: number;
      available: number;
      unit: string;
    }[];

    // Effective ingredients = recipe ingredients with overrides applied.
    const ingredientMap = new Map<string, BatchIngredientOverride>();
    for (const ri of selectedRecipe?.ingredients ?? []) {
      ingredientMap.set(ri.id, {
        recipe_ingredient_id: ri.id,
        raw_material_id: ri.raw_material_id || undefined,
        oil_type_id: ri.oil_type_id || undefined,
        quantity_per_box: ri.quantity_per_box,
        waste_factor: ri.waste_factor,
        is_removed: false,
      });
    }
    for (const override of ingredientOverrides) {
      if (override.recipe_ingredient_id) {
        if (override.is_removed) {
          ingredientMap.delete(override.recipe_ingredient_id);
        } else {
          ingredientMap.set(override.recipe_ingredient_id, override);
        }
      } else {
        const tempId = `new-${override.oil_type_id || override.raw_material_id}`;
        ingredientMap.set(tempId, override);
      }
    }

    const offending: {
      key: string;
      name: string;
      needed: number;
      available: number;
      unit: string;
    }[] = [];

    for (const [key, ing] of ingredientMap.entries()) {
      const wasteFactor = ing.waste_factor || 1.0;
      const needed = ing.quantity_per_box * wasteFactor * formData.target_quantity;
      if (needed <= 0) continue;

      if (ing.oil_type_id) {
        const oil = oilTypes.find((o) => o.id === ing.oil_type_id);
        if (!oil || !oil.density_kg_per_liter) continue;
        const availableKg =
          getStockAt(oilStockByPlant, ing.oil_type_id, plant) *
          oil.density_kg_per_liter;
        if (needed > availableKg) {
          offending.push({
            key,
            name: oil.code ? `${oil.code} - ${oil.name}` : oil.name,
            needed,
            available: availableKg,
            unit: "kg",
          });
        }
      } else if (ing.raw_material_id) {
        const material = rawMaterials.find((m) => m.id === ing.raw_material_id);
        if (!material) continue;
        const available = getStockAt(
          rawStockByPlant,
          ing.raw_material_id,
          plant
        );
        if (needed > available) {
          offending.push({
            key,
            name: material.code
              ? `${material.code} - ${material.name}`
              : material.name,
            needed,
            available,
            unit: UNIT_LABELS[material.unit] || material.unit,
          });
        }
      }
    }

    return offending;
  }, [
    formData.plant,
    formData.target_quantity,
    selectedRecipe?.ingredients,
    ingredientOverrides,
    oilTypes,
    rawMaterials,
    oilStockByPlant,
    rawStockByPlant,
  ]);

  const hasOverStock = overStockIngredients.length > 0;

  // Reset the acknowledgement whenever the offending set changes.
  useEffect(() => {
    setAckOverStock(false);
  }, [overStockIngredients]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) {
      toast({
        title: "Error",
        description: "No se pudo identificar el usuario",
        variant: "destructive",
      });
      return;
    }

    if (!formData.product_id) {
      toast({
        title: "Error",
        description: "Debe seleccionar un producto",
        variant: "destructive",
      });
      return;
    }

    if (!formData.assembly_line) {
      toast({
        title: "Error",
        description: "Debe seleccionar una línea de envasado",
        variant: "destructive",
      });
      return;
    }

    if (!formData.plant) {
      toast({
        title: "Error",
        description: "Debe seleccionar una planta",
        variant: "destructive",
      });
      return;
    }

    // Stock validation - warn but allow
    if (hasInsufficientStock) {
      const confirmed = await showConfirm({
        title: "Stock insuficiente",
        text: "Uno o más ingredientes no tienen stock suficiente. ¿Desea continuar de todas formas?",
        icon: "warning",
        confirmButtonText: "Sí, continuar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmed.isConfirmed) return;
    }

    createMutation.mutate(
      {
        data: { ...formData, ingredient_overrides: ingredientOverrides },
        createdBy: userId,
      },
      {
        onSuccess: (batch) => {
          toast({
            title: "Lote creado",
            description: `Lote ${batch.batch_number} creado exitosamente`,
          });
          navigate(`/production/batches/${batch.id}`);
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: `No se pudo crear el lote: ${error.message}`,
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <div className="container mx-auto max-h-full w-[80vw] space-y-6 py-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/production/batches")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Package className="h-6 w-6" />
            Nuevo Lote de Producción
          </h1>
          <p className="text-muted-foreground">
            Crear un nuevo lote de producción semanal
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-3 gap-6">
          {/* Left column: Main form */}
          <div className="col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Información del Lote
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="product_id">Producto *</Label>
                    <SearchableSelect
                      value={selectedProductId}
                      onValueChange={setSelectedProductId}
                      options={products.map((p) => ({
                        value: p.id,
                        label: p.sku ? `${p.sku} - ${p.short_name}` : p.short_name,
                        searchTerms: `${p.sku || ""} ${p.short_name} ${p.barcode || ""}`,
                      }))}
                      placeholder="Seleccionar producto"
                      searchPlaceholder="Buscar producto..."
                      emptyMessage="No se encontraron productos"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="recipe_id">Receta</Label>
                    <Select
                      value={formData.recipe_id || "none"}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          recipe_id: value === "none" ? undefined : value,
                        }))
                      }
                      disabled={
                        !selectedProductId || productRecipes.length === 0
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sin receta" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin receta</SelectItem>
                        {productRecipes.map((recipe) => (
                          <SelectItem key={recipe.id} value={recipe.id}>
                            {recipe.name}
                            {recipe.is_default && " (Por defecto)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="target_quantity">
                    Cantidad Objetivo (
                    {getProductionUnitLabel(selectedProduct?.unit_code, 2)}) *
                  </Label>
                  <Input
                    id="target_quantity"
                    type="number"
                    min="1"
                    value={formData.target_quantity}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        target_quantity: parseInt(e.target.value) || 0,
                      }))
                    }
                    required
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Fecha Inicio *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          start_date: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_date">Fecha Fin *</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      min={formData.start_date}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          end_date: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expiry_date">Fecha Vencimiento *</Label>
                    <Input
                      id="expiry_date"
                      type="date"
                      value={formData.expiry_date}
                      min={formData.end_date}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          expiry_date: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="assembly_line">Línea de Envasado *</Label>
                    <Select
                      value={formData.assembly_line || ""}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          assembly_line: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar línea" />
                      </SelectTrigger>
                      <SelectContent>
                        {assemblyLines.map((line) => (
                          <SelectItem key={line.id} value={line.code}>
                            {line.name
                              ? `${line.name} (${line.code})`
                              : line.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="plant">Planta *</Label>
                    <Select
                      value={formData.plant || ""}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          plant: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar planta" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="P1">Planta 1 (P1)</SelectItem>
                        <SelectItem value="P2">Planta 2 (P2)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Lote Generado</Label>
                    <div className="flex h-10 items-center rounded-md border bg-muted px-3 font-mono text-sm">
                      {formData.assembly_line && formData.plant
                        ? `${dayjs(formData.start_date).format("DDMMYYYY")}${formData.assembly_line}${formData.plant}`
                        : "-"}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="contains_allergens"
                    checked={formData.contains_allergens}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        contains_allergens: checked === true,
                      }))
                    }
                  />
                  <Label
                    htmlFor="contains_allergens"
                    className="flex cursor-pointer items-center gap-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    Contiene alergenos
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notas</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    placeholder="Notas adicionales sobre el lote..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Editable ingredients table */}
            {selectedProductId && (
              <BatchIngredientsEditor
                recipeIngredients={selectedRecipe?.ingredients || []}
                overrides={ingredientOverrides}
                onChange={setIngredientOverrides}
                targetQuantity={formData.target_quantity}
                onStockStatusChange={setHasInsufficientStock}
              />
            )}
          </div>

          {/* Right column: Cost summary */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calculator className="h-4 w-4" />
                  Resumen de Costos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Tipo de Cambio:
                    </span>
                    <span className="font-medium">
                      ${formatCurrency(latestRate)} / USD
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Costo por{" "}
                      {selectedProduct?.unit_code === "UNI" ? "Unidad" : "Caja"}
                      :
                    </span>
                    <span className="font-medium">
                      {costPerBoxClp > 0
                        ? `$${formatCurrency(costPerBoxClp)}`
                        : "-"}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Cantidad:</span>
                    <span className="font-medium">
                      {formData.target_quantity}{" "}
                      {getProductionUnitLabel(
                        selectedProduct?.unit_code,
                        formData.target_quantity
                      )}
                    </span>
                  </div>

                  <hr />

                  <div className="flex justify-between">
                    <span className="font-medium">Total Estimado (CLP):</span>
                    <span className="text-lg font-bold">
                      {estimatedCostClp > 0
                        ? `$${formatCurrency(estimatedCostClp)}`
                        : "-"}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Total Estimado (USD):
                    </span>
                    <span className="font-medium">
                      {estimatedCostUsd > 0 ? formatUsd(estimatedCostUsd) : "-"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {hasOverStock && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-2 text-sm">
                    <p className="font-medium">
                      El consumo planificado supera el stock disponible en{" "}
                      {formData.plant}
                    </p>
                    <ul className="space-y-1">
                      {overStockIngredients.map((ing) => (
                        <li key={ing.key} className="tabular-nums">
                          <span className="font-medium">{ing.name}</span>:
                          necesita {ing.needed.toFixed(2)} {ing.unit} · disponible{" "}
                          {ing.available.toFixed(2)} {ing.unit}
                        </li>
                      ))}
                    </ul>
                    <label className="flex cursor-pointer items-start gap-2 pt-1">
                      <Checkbox
                        checked={ackOverStock}
                        onCheckedChange={(checked) =>
                          setAckOverStock(checked === true)
                        }
                        className="mt-0.5 border-amber-400 data-[state=checked]:bg-amber-600 data-[state=checked]:text-white"
                      />
                      <span className="text-sm font-medium">
                        Entiendo que el consumo supera el stock disponible
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                className="w-full"
                disabled={
                  createMutation.isPending ||
                  !formData.product_id ||
                  !formData.assembly_line ||
                  !formData.plant ||
                  (hasOverStock && !ackOverStock)
                }
              >
                {createMutation.isPending ? "Creando..." : "Crear Lote"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate("/production/batches")}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
